import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { PendingSettingTypeChange, SettingsService, SettingType } from '../../core/settings/settings.service';
import { EnvironmentService } from '../../core/environment/environment.service';
import { SessionService } from '../../auth/session/session.service';
import { NewSettingModalComponent } from '../new-setting-modal/new-setting-modal.component';
import { PromoteSettingModalComponent } from '../promote-setting-modal/promote-setting-modal.component';

@Component({
  selector: 'settings-types',
  templateUrl: './settings-types.component.html',
  styleUrls: ['./settings-types.component.scss']
})
export class SettingsTypesComponent implements OnInit, OnDestroy {
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true
  };

  searchText = '';
  canPromote = false;
  isDevEnv = false;
  defaultGridRefreshDelay = 5000; // 5 second delay to allow backend to process changes before refreshing grid
  private gridApi!: GridApi<SettingType>;
  private allSettingTypes: SettingType[] = [];
  private resizeListener?: () => void;
  private promotableSettings: Set<string> = new Set();
  // settingName -> its open change awaiting approval in the higher environment (prod).
  private pendingChanges: Map<string, PendingSettingTypeChange> = new Map();
  private currentUserName = '';


  constructor(
    private readonly settingsService: SettingsService,
    private readonly environmentService: EnvironmentService,
    private readonly sessionService: SessionService,
    private readonly modalService: NgbModal,
    private readonly router: Router,
    private readonly toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.canPromote = this.environmentService.canPromote();
    this.isDevEnv = this.environmentService.getCurrentEnvironment() === 'dev';
    this.currentUserName = this.sessionService.getCurrentSession()?.name ?? '';
    this.columnDefs = [
      {
        headerName: 'Name',
        field: 'label',
        flex: 2
      },
      {
        headerName: 'Category',
        field: 'category',
        flex: 1
      }
    ];

    // Add actions column if in dev or stg
    if (this.canPromote) {
      this.columnDefs.push({
        headerName: 'Actions',
        field: 'actions',
        flex: 1,
        cellRenderer: this.actionsCellRenderer.bind(this),
        suppressSizeToFit: true,
        minWidth: 150
      });
    }

    // Audit trail is read-only history and is available in every environment (including prod, where
    // the Actions column is hidden), so this column is always present, to the right of Actions.
    this.columnDefs.push({
      headerName: 'Audit',
      field: 'audit',
      flex: 1,
      cellRenderer: this.auditCellRenderer.bind(this),
      suppressSizeToFit: true,
      minWidth: 100
    });
  }

  onGridReady(params: GridReadyEvent<SettingType>): void {
    this.gridApi = params.api;
    this.loadSettingTypes();

    this.resizeListener = () => {
      setTimeout(() => {
        params.api.sizeColumnsToFit();
      });
    };
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  onSearchChange(): void {
    if (this.gridApi) {
      this.filterGrid();
    }
  }

  onRowClicked(event: RowClickedEvent<SettingType>): void {
    // Don't open edit modal if clicking on the actions column
    const target = event.event?.target as HTMLElement;
    if (target?.closest('.actions-cell') && this.canPromote) {
      return;
    }

    // The Audit link renders in every environment, so guard it independently of canPromote.
    if (target?.closest('.audit-cell')) {
      return;
    }

    if (event.data) {
      this.openEditSettingModal(event.data);
    }
  }

  async openCreateNewSettingModal(): Promise<void> {
    const modal = this.modalService.open(NewSettingModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true
    });

    try {
      await modal.result;
      await this.refreshGrid(this.defaultGridRefreshDelay); // Delay to allow backend to process new setting before refreshing
    } catch (e) {
      // Modal dismissed
    }
  }

  async openEditSettingModal(settingType: SettingType): Promise<void> {
    const modal = this.modalService.open(NewSettingModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true
    });
    modal.componentInstance.settingToEdit = settingType;

    try {
      await modal.result;
      await this.refreshGrid(this.defaultGridRefreshDelay); // Delay to allow backend to process new setting before refreshing
    } catch (e) {
      // Modal dismissed
    }
  }

  async openPromoteSettingModal(settingType: SettingType): Promise<void> {
    const modal = this.modalService.open(PromoteSettingModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true
    });
    modal.componentInstance.settingToPromote = settingType;

    try {
      const result = await modal.result;
      if (result) {
        // Refresh to update promotable status
        await this.refreshGrid(this.defaultGridRefreshDelay); // Delay to allow backend to process new setting before refreshing
      }
    } catch (e) {
      // Modal dismissed
    }
  }

  async onRefresh(): Promise<void> {
    await this.refreshGrid();
  }

    private actionsCellRenderer(params: any): HTMLElement {
    const container = document.createElement('div');
    container.className = 'actions-cell';

    const pending = this.pendingChanges.get(params.data.name);

    // A change awaiting approval in the higher env blocks stacking another promotion. The
    // requester can only watch it; a different Setting Type Admin gets an Approve affordance.
    if (pending) {
      // The requester can only watch their own pending change; a different admin gets Approve.
      if (this.isSameUser(pending.requester, this.currentUserName)) {
        const tag = document.createElement('span');
        tag.className = 'pending-approval-tag';
        tag.textContent = 'Pending approval';
        container.appendChild(tag);
      } else {
        const approveBtn = document.createElement('button');
        approveBtn.className = 'btn-promote-small';
        approveBtn.innerHTML = '<i class="lx-icon lx-check"></i> Approve';
        approveBtn.onclick = (e) => {
          e.stopPropagation();
          this.openApproveSettingModal(pending);
        };
        container.appendChild(approveBtn);
      }
      return container;
    }

    const isPromotable = this.promotableSettings.has(params.data.name);

    if (isPromotable) {
      const promoteBtn = document.createElement('button');
      promoteBtn.className = 'btn-promote-small';
      promoteBtn.innerHTML = '<i class="lx-icon lx-upload"></i> Promote';
      promoteBtn.onclick = (e) => {
        e.stopPropagation();
        this.openPromoteSettingModal(params.data);
      };
      container.appendChild(promoteBtn);
    } else {
      const span = document.createElement('span');
      span.className = 'no-action';
      span.textContent = 'Already in higher env';
      container.appendChild(span);
    }

    return container;
  }

  private isSameUser(a: string, b: string): boolean {
    return !!a && !!b && a.toLowerCase() === b.toLowerCase();
  }

  private auditCellRenderer(params: any): HTMLElement {
    const container = document.createElement('div');
    container.className = 'audit-cell';

    const auditLink = document.createElement('a');
    auditLink.href = '#';
    auditLink.className = 'audit-link';
    auditLink.textContent = 'Audit';
    auditLink.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // params.data.name is the real setting key; the grid displays label.
      this.router.navigate(['/settings/types', params.data.name, 'audit']);
    };
    container.appendChild(auditLink);

    return container;
  }

  async openApproveSettingModal(pending: PendingSettingTypeChange): Promise<void> {
    const modal = this.modalService.open(PromoteSettingModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true
    });
    // Show exactly what will be applied on approval — the change request's proposed definition,
    // which the backend applies verbatim — not the local grid row (which can have drifted since
    // the promotion was submitted). previousDefinition drives the before/after diff.
    modal.componentInstance.settingToPromote = pending.proposedDefinition;
    modal.componentInstance.previousSetting = pending.previousDefinition;
    modal.componentInstance.mode = 'review';
    modal.componentInstance.requestId = pending.id;

    try {
      const result = await modal.result;
      if (result) {
        await this.refreshGrid(this.defaultGridRefreshDelay);
      }
    } catch (e) {
      // Modal dismissed
    }
  }

  private async loadSettingTypes(): Promise<void> {
    try {
      this.gridApi?.showLoadingOverlay();

      this.allSettingTypes = this.settingsService.getAllSettingTypes();

      if (this.canPromote) {
        await Promise.all([this.checkPromotableSettings(), this.loadPendingChanges()]);
      }
      
      if (this.allSettingTypes.length === 0) {
        this.gridApi?.showNoRowsOverlay();
      } else {
        if (this.searchText.trim()) {
          this.filterGrid();
        } else {
          this.gridApi?.setRowData(this.allSettingTypes);
        }
        this.gridApi?.hideOverlay();
      }
      
      this.gridApi?.sizeColumnsToFit();
    } catch (error) {
      console.error('Error loading setting types:', error);
      this.toastr.error('Failed to load setting types.');
      this.gridApi?.showNoRowsOverlay();
    }
  }

  private async checkPromotableSettings(): Promise<void> {
    this.promotableSettings.clear();
    
    const checkPromises = this.allSettingTypes.map(async (setting) => {
      const existsInHigher = await this.settingsService.checkSettingExistsInHigherEnvironment(setting);
      if (!existsInHigher) {
        this.promotableSettings.add(setting.name);
      }
    });

    await Promise.all(checkPromises);
  }

  /**
   * Loads setting-type changes awaiting approval in the higher environment (prod). Only prod parks
   * changes for approval, so this is populated on the stg grid and empty elsewhere. A fetch failure
   * is non-fatal — the grid still renders, just without pending tags.
   */
  private async loadPendingChanges(): Promise<void> {
    this.pendingChanges.clear();

    const higherEnvConfig = this.environmentService.getHigherEnvironmentConfig();
    if (!higherEnvConfig) {
      return;
    }

    try {
      const pending = await this.settingsService.getPendingSettingTypeChanges(higherEnvConfig.settingsUpdate);
      (pending ?? []).forEach((change) => this.pendingChanges.set(change.settingName, change));
    } catch (error) {
      console.error('Error loading pending setting type changes:', error);
    }
  }

  private async refreshGrid(delay?: number): Promise<void> {
    this.gridApi?.showLoadingOverlay();
    if(delay) await new Promise(resolve => setTimeout(resolve, delay));

    try {
      this.settingsService.clearSettingTypesCache();
      await this.settingsService.initializeSettingTypes();
      await this.loadSettingTypes();
    } catch (error) {
      console.error('Error refreshing setting types:', error);
      this.toastr.error('Failed to refresh setting types.');
    }
  }

  private filterGrid(): void {
    if (!this.searchText.trim()) {
      this.gridApi?.setRowData(this.allSettingTypes);
      return;
    }

    const searchLower = this.searchText.toLowerCase();
    const filteredData = this.allSettingTypes.filter(settingType =>
      settingType.label?.toLowerCase().includes(searchLower) ||
      settingType.name?.toLowerCase().includes(searchLower)
    );

    this.gridApi?.setRowData(filteredData);
  }
}
